import { ensureGsap, prefersReducedMotion } from "@theme/vp-motion";
import { buildVehicleFilterUrl, cosyFromCache, displayLabelForVehicle, isCompleteKenteken, loadVehicle, vehicleFilterBaseUrl, vehicleFilterModels } from "@theme/kenteken-api";
import { isVehicleCosyImageUrl, managedCosyForVehicle } from "@theme/vp-model-cosy-kentekens";
import { computeCosyFit, isUsableCosyImage } from "@theme/vp-cosy-fit";
//#region assets/vp-sticky-kenteken.ts
/**
* Sticky kenteken FAB — popup flies up from the bottom with a spring + stagger.
* On PDP, smoothly lifts above the sticky add-to-cart bar when it appears.
*/
/** Soft spring — elastic with a longer period so it settles without wild bounce. */
var SPRING = "elastic.out(1, 0.62)";
var SPRING_SOFT = "elastic.out(1, 0.78)";
/** Gap between sticky ATC bar top and the FAB when lifted — match chat-drawer ATC_CHAT_GAP. */
var ATC_LIFT_GAP = 16;
var VpStickyKenteken = class extends HTMLElement {
	/** @type {typeof window.gsap | null} */
	#gsap = null;
	/** @type {gsap.core.Timeline | null} */
	#timeline = null;
	/** @type {gsap.core.Tween | null} */
	#liftTween = null;
	/** @type {boolean} */
	#open = false;
	/** @type {HTMLElement | null} */
	#lastFocus = null;
	/** @type {HTMLButtonElement | null} */
	#fab = null;
	/** @type {HTMLElement | null} */
	#fabIcon = null;
	/** @type {HTMLElement | null} */
	#fabMedia = null;
	/** @type {HTMLImageElement | null} */
	#fabImage = null;
	/** @type {number} */
	#fabImageToken = 0;
	/** @type {HTMLElement | null} */
	#popup = null;
	/** @type {HTMLElement | null} */
	#backdrop = null;
	/** @type {HTMLButtonElement | null} */
	#closeBtn = null;
	/** @type {HTMLAnchorElement | null} */
	#assortmentCta = null;
	/** @type {string} */
	#warnedMissingMapping = "";
	/** @type {HTMLElement | null} */
	#atcBar = null;
	/** @type {MutationObserver | null} */
	#atcAttrObserver = null;
	/** @type {MediaQueryList | null} */
	#desktopMq = null;
	/** @type {number} */
	#liftPx = 0;
	/** @type {{ lift: number }} */
	#liftState = { lift: 0 };
	/** @type {ReturnType<typeof setTimeout> | null} */
	#revealHoldTimer = null;
	/** @type {(() => void) | null} */
	#revealHoldResolve = null;
	/** @type {number} */
	#revealGeneration = 0;
	/** @type {(event: Event) => void} */
	#onAtcVisibility = (event) => {
		const detail = event.detail || {};
		if (detail.bar instanceof HTMLElement) this.#atcBar = detail.bar;
		this.#syncAtcLift({
			stuck: Boolean(detail.stuck),
			immediate: prefersReducedMotion()
		});
	};
	/** @type {(event: MediaQueryListEvent) => void} */
	#onDesktopMqChange = () => {
		this.#syncAtcLift({ immediate: true });
	};
	/** @type {(event: Event) => void} */
	#onVehicleChange = (event) => {
		const vehicle = event.detail?.vehicle ?? null;
		this.#syncFabMedia(vehicle);
		this.#syncAssortmentCta(vehicle);
		if (!this.#open) return;
		if (this.dataset.plpFilter !== "true") return;
		this.#applyPlpFilters(vehicle);
	};
	#onVehicleInput = () => {
		this.#hideAssortmentCta();
	};
	connectedCallback() {
		this.#fab = this.querySelector("[data-vp-sticky-kenteken-fab]");
		this.#fabIcon = this.querySelector("[data-vp-sticky-kenteken-fab-icon]");
		this.#fabMedia = this.querySelector("[data-vp-sticky-kenteken-fab-media]");
		this.#fabImage = this.querySelector("[data-vp-sticky-kenteken-fab-image]");
		this.#popup = this.querySelector("[data-vp-sticky-kenteken-popup]");
		this.#backdrop = this.querySelector("[data-vp-sticky-kenteken-backdrop]");
		this.#closeBtn = this.querySelector("[data-vp-sticky-kenteken-close]");
		this.#assortmentCta = this.querySelector("[data-vp-sticky-kenteken-assortment]");
		if (!this.#fab || !this.#popup || !this.#backdrop || !this.#closeBtn) return;
		this.#fab.addEventListener("click", () => this.open());
		this.#closeBtn.addEventListener("click", () => this.close());
		this.#backdrop.addEventListener("click", () => this.close());
		this.addEventListener("keydown", (event) => this.#onKeydown(event));
		this.addEventListener("input", this.#onVehicleInput, true);
		this.addEventListener("vp-model-select:change", this.#onVehicleInput, true);
		this.#fabImage?.addEventListener("error", () => {
			this.#fabImageToken += 1;
			this.#showFabIcon();
		});
		this.#desktopMq = window.matchMedia("(min-width: 750px)");
		this.#desktopMq.addEventListener("change", this.#onDesktopMqChange);
		window.addEventListener("vp-sticky-atc:visibility", this.#onAtcVisibility);
		window.addEventListener("vp-vehicle:change", this.#onVehicleChange);
		this.#setClosedChrome();
		const vehicle = loadVehicle();
		this.#syncFabMedia(vehicle);
		this.#syncAssortmentCta(vehicle);
		ensureGsap().then((gsap) => {
			this.#gsap = gsap;
		});
		this.#bindAtcBar();
		requestAnimationFrame(() => {
			this.#bindAtcBar();
			this.#syncAtcLift({ immediate: true });
		});
	}
	disconnectedCallback() {
		this.#cancelRevealHold();
		this.#desktopMq?.removeEventListener("change", this.#onDesktopMqChange);
		window.removeEventListener("vp-sticky-atc:visibility", this.#onAtcVisibility);
		window.removeEventListener("vp-vehicle:change", this.#onVehicleChange);
		this.removeEventListener("input", this.#onVehicleInput, true);
		this.removeEventListener("vp-model-select:change", this.#onVehicleInput, true);
		this.#atcAttrObserver?.disconnect();
		this.#atcAttrObserver = null;
		this.#atcBar = null;
		this.#liftTween?.kill();
		this.#liftTween = null;
		this.#timeline?.kill();
		this.#timeline = null;
	}
	get isOpen() {
		return this.#open;
	}
	/**
	* Open to show the selected vehicle, hold briefly, then auto-close.
	* Used after a PLP filter-panel vehicle selection. Skips if already open.
	* @param {{ holdMs?: number }} [options]
	* @returns {Promise<void>}
	*/
	async revealVehicle({ holdMs = 2200 } = {}) {
		if (this.#open || !this.#fab || !this.#popup || !this.#backdrop) return;
		const generation = ++this.#revealGeneration;
		this.#cancelRevealHold();
		this.open();
		const hold = prefersReducedMotion() ? Math.min(holdMs, 600) : holdMs;
		await new Promise((resolve) => {
			this.#revealHoldResolve = resolve;
			this.#revealHoldTimer = setTimeout(() => {
				this.#revealHoldTimer = null;
				this.#revealHoldResolve = null;
				resolve();
			}, hold);
		});
		if (generation !== this.#revealGeneration) return;
		if (!this.#open) return;
		this.close();
	}
	open() {
		if (this.#open || !this.#fab || !this.#popup || !this.#backdrop) return;
		this.#cancelRevealHold();
		this.#open = true;
		this.#lastFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
		this.setAttribute("data-open", "");
		this.#fab.setAttribute("aria-expanded", "true");
		this.#popup.hidden = false;
		this.#popup.removeAttribute("inert");
		this.#backdrop.hidden = false;
		this.#animateOpen();
	}
	close() {
		if (!this.#open || !this.#fab || !this.#popup || !this.#backdrop) return;
		this.#cancelRevealHold();
		this.#revealGeneration += 1;
		this.#open = false;
		this.#fab.setAttribute("aria-expanded", "false");
		this.#backdrop.style.pointerEvents = "none";
		this.#popup.setAttribute("inert", "");
		this.#animateClose().then(() => {
			if (this.#open) return;
			this.#setClosedChrome();
			this.#restoreFocus();
		});
	}
	#cancelRevealHold() {
		if (this.#revealHoldTimer != null) {
			clearTimeout(this.#revealHoldTimer);
			this.#revealHoldTimer = null;
		}
		if (this.#revealHoldResolve) {
			const resolve = this.#revealHoldResolve;
			this.#revealHoldResolve = null;
			resolve();
		}
	}
	#setClosedChrome() {
		this.removeAttribute("data-open");
		if (this.#popup) {
			this.#popup.hidden = true;
			this.#popup.setAttribute("inert", "");
			this.#popup.style.opacity = "";
			this.#popup.style.transform = "";
			this.#popup.style.pointerEvents = "";
		}
		if (this.#backdrop) {
			this.#backdrop.hidden = true;
			this.#backdrop.style.opacity = "";
			this.#backdrop.style.pointerEvents = "";
		}
		if (this.#fab) {
			this.#fab.style.visibility = "";
			this.#fab.style.pointerEvents = "";
			this.#fab.style.opacity = "";
			this.#fab.style.transform = "";
		}
	}
	#bindAtcBar() {
		const bar = document.querySelector(".vp-sticky-atc__bar") || document.querySelector(".sticky-add-to-cart__bar");
		if (!(bar instanceof HTMLElement)) return;
		if (bar !== this.#atcBar) {
			this.#atcAttrObserver?.disconnect();
			this.#atcBar = bar;
			this.#atcAttrObserver = new MutationObserver(() => {
				this.#syncAtcLift({ immediate: prefersReducedMotion() });
			});
			this.#atcAttrObserver.observe(bar, {
				attributes: true,
				attributeFilter: ["data-stuck"]
			});
		}
	}
	/**
	* @param {import('@theme/kenteken-api').VpVehicle|null} [vehicle]
	*/
	async #syncFabMedia(vehicle = loadVehicle()) {
		if (!this.#fab || !this.#fabIcon || !this.#fabMedia || !this.#fabImage) return;
		if (!vehicleQualifiesForFabMedia(vehicle)) {
			this.#fabImageToken += 1;
			this.#showFabIcon();
			return;
		}
		const token = this.#fabImageToken += 1;
		const cosyImages = readCosyImagesFromPanel(this.querySelector("[data-kenteken-panel]"));
		let url = this.#resolveFabImageUrl(vehicle);
		if (!url) url = managedCosyForVehicle(vehicle, cosyImages);
		if (token !== this.#fabImageToken) return;
		const candidates = this.#fabImageCandidates(vehicle, url);
		if (!candidates.length) {
			this.#showFabPlaceholder(vehicle);
			return;
		}
		let finalUrl = "";
		for (const candidate of candidates) {
			const normalized = normalizeFabImageUrl(candidate);
			if (this.hasAttribute("data-has-kenteken") && this.#fabImage.getAttribute("src") === normalized) {
				this.#fabImage.alt = displayLabelForVehicle(vehicle) || "Uw voertuig";
				if (!this.#fabImage.style.transform) await this.#applyFabCosyFit(normalized);
				return;
			}
			try {
				await preloadFabImage(normalized);
				if (isVehicleCosyImageUrl(normalized, cosyImages) && !await isUsableCosyImage(normalized)) continue;
				finalUrl = normalized;
				break;
			} catch {
				if (token !== this.#fabImageToken) return;
			}
		}
		if (token !== this.#fabImageToken) return;
		if (!finalUrl) {
			this.#showFabPlaceholder(vehicle);
			return;
		}
		const current = loadVehicle();
		if (!vehicleQualifiesForFabMedia(current)) {
			this.#showFabIcon();
			return;
		}
		await this.#applyFabCosyFit(finalUrl);
		if (token !== this.#fabImageToken) return;
		this.#fabImage.alt = displayLabelForVehicle(current) || "Uw voertuig";
		this.#fabImage.referrerPolicy = "no-referrer";
		this.#fabImage.src = finalUrl;
		this.#fabMedia.hidden = false;
		this.removeAttribute("data-fab-placeholder");
		this.setAttribute("data-has-kenteken", "");
	}
	/**
	* Cosy renders pad each model differently — scale the car's measured bbox to
	* a consistent size inside the round badge (see vp-cosy-fit.ts).
	* @param {string} url
	*/
	async #applyFabCosyFit(url) {
		if (!this.#fabImage) return;
		const fit = await computeCosyFit(url, {
			boxAspect: 1,
			width: .88,
			maxHeight: .5
		});
		this.#fabImage.style.transform = fit ? `translate(${fit.txPct}%, ${fit.tyPct}%) scale(${fit.scale})` : "";
	}
	#showFabIcon() {
		this.removeAttribute("data-has-kenteken");
		this.removeAttribute("data-fab-placeholder");
		if (this.#fabMedia) this.#fabMedia.hidden = true;
		if (this.#fabImage) {
			this.#fabImage.removeAttribute("src");
			this.#fabImage.style.transform = "";
		}
	}
	/** Show selected-vehicle fallback when no Cosy image exists. */
	#showFabPlaceholder(vehicle) {
		if (!this.#fabImage || !this.#fabMedia || !vehicleQualifiesForFabMedia(vehicle)) {
			this.#showFabIcon();
			return;
		}
		const url = String(this.dataset.emptyCosyUrl || "").trim();
		if (!url) {
			this.#showFabIcon();
			return;
		}
		this.#fabImage.alt = "";
		this.#fabImage.style.transform = "";
		this.#fabImage.src = normalizeFabImageUrl(url);
		this.#fabMedia.hidden = false;
		this.setAttribute("data-fab-placeholder", "");
		this.setAttribute("data-has-kenteken", "");
	}
	/**
	* Live Cosy URLs first; merchant-managed generation image when they fail.
	*
	* @param {import('@theme/kenteken-api').VpVehicle} vehicle
	* @param {string} [primaryUrl]
	* @returns {string[]}
	*/
	#fabImageCandidates(vehicle, primaryUrl = "") {
		/** @type {string[]} */
		const candidates = [];
		const add = (url) => {
			const value = String(url || "").trim();
			if (!value || candidates.includes(value)) return;
			candidates.push(value);
		};
		add(primaryUrl);
		const panel = this.querySelector("[data-kenteken-panel]");
		add(managedCosyForVehicle(vehicle, readCosyImagesFromPanel(panel)));
		return candidates;
	}
	/**
	* @param {import('@theme/kenteken-api').VpVehicle} vehicle
	* @returns {string}
	*/
	#resolveFabImageUrl(vehicle) {
		if (vehicle.imageUrl) return vehicle.imageUrl;
		if (vehicle.brandAndModel) {
			const cached = cosyFromCache(vehicle.brandAndModel);
			if (cached) return cached;
		}
		return "";
	}
	/**
	* Apply vehicle facets on collection/search via the page facets form.
	* @param {import('@theme/kenteken-api').VpVehicle | null} vehicle
	*/
	#applyPlpFilters(vehicle) {
		const facetsForm = document.querySelector("facets-form-component");
		if (!facetsForm || typeof facetsForm.updateFiltersByURL !== "function") return;
		const base = vehicleFilterBaseUrl(vehicle, `${window.location.pathname}${window.location.search}`, { searchToAll: true });
		const url = buildVehicleFilterUrl(vehicle || {
			merk: null,
			brandAndModel: null,
			brandAndModels: []
		}, base);
		try {
			const next = new URL(url, window.location.origin);
			if (next.pathname !== window.location.pathname) {
				window.location.assign(`${next.pathname}${next.search}`);
				return;
			}
		} catch {
			window.location.assign(url);
			return;
		}
		facetsForm.updateFiltersByURL(url);
	}
	#hideAssortmentCta() {
		if (!this.#assortmentCta) return;
		this.#assortmentCta.hidden = true;
		this.#assortmentCta.removeAttribute("href");
	}
	/**
	* Keep the catalog CTA aligned with the stored vehicle. Unknown mappings
	* must not degrade to a brand-only URL: that would show incompatible items.
	* @param {import('@theme/kenteken-api').VpVehicle | null} vehicle
	*/
	#syncAssortmentCta(vehicle) {
		if (!this.#assortmentCta) return;
		const models = vehicleFilterModels(vehicle);
		if (!vehicle || !models.length) {
			this.#hideAssortmentCta();
			if (vehicle) {
				const warningKey = [
					vehicle.merk,
					vehicle.modelLabel,
					vehicle.chassisCode
				].filter(Boolean).join("|");
				if (warningKey && warningKey !== this.#warnedMissingMapping) {
					this.#warnedMissingMapping = warningKey;
					console.warn("[vp-sticky-kenteken] Missing storefront model mapping; hiding vehicle assortment CTA.");
				}
			}
			return;
		}
		this.#warnedMissingMapping = "";
		this.#assortmentCta.href = buildVehicleFilterUrl(vehicle, "/collections/all");
		this.#assortmentCta.hidden = false;
	}
	/**
	* @param {boolean} [stuck]
	* @returns {number}
	*/
	#targetLiftPx(stuck) {
		if (!this.#desktopMq?.matches) return 0;
		if (!(stuck ?? this.#atcBar?.dataset.stuck === "true") || !this.#atcBar) return 0;
		if (getComputedStyle(this.#atcBar).display === "none") return 0;
		const styles = getComputedStyle(this);
		const inset = Number.parseFloat(styles.getPropertyValue("--vp-sticky-kenteken-inset-block")) || Number.parseFloat(styles.getPropertyValue("--vp-sticky-kenteken-inset")) || 16;
		const barHeight = this.#atcBar.offsetHeight || this.#atcBar.scrollHeight || this.#atcBar.getBoundingClientRect().height || 112;
		return Math.max(0, Math.round(barHeight + ATC_LIFT_GAP - inset));
	}
	/**
	* Animate lift via CSS var (no transform on host — keeps position:fixed backdrop
	* viewport-relative and avoids breaking kenteken hit-testing).
	* @param {{ stuck?: boolean, immediate?: boolean }} [options]
	*/
	async #syncAtcLift({ stuck, immediate = false } = {}) {
		this.#bindAtcBar();
		const next = this.#targetLiftPx(stuck);
		if (next === this.#liftPx && !this.#liftTween?.isActive?.()) {
			this.style.setProperty("--vp-sticky-kenteken-lift", `${next}px`);
			return;
		}
		this.#liftPx = next;
		this.#liftTween?.kill();
		if (immediate || prefersReducedMotion()) {
			this.#liftState.lift = next;
			this.style.setProperty("--vp-sticky-kenteken-lift", `${next}px`);
			return;
		}
		if (!this.#gsap) this.#gsap = await ensureGsap();
		const gsap = this.#gsap;
		if (!gsap) {
			this.#liftState.lift = next;
			this.style.setProperty("--vp-sticky-kenteken-lift", `${next}px`);
			return;
		}
		this.#liftTween = gsap.to(this.#liftState, {
			lift: next,
			duration: .45,
			ease: "power3.out",
			onUpdate: () => {
				this.style.setProperty("--vp-sticky-kenteken-lift", `${this.#liftState.lift}px`);
			}
		});
	}
	/**
	* @param {KeyboardEvent} event
	*/
	#onKeydown(event) {
		if (!this.#open) return;
		if (event.key === "Escape") {
			event.preventDefault();
			this.close();
			return;
		}
		if (event.key !== "Tab" || !this.#popup) return;
		const focusable = this.#focusableElements();
		if (focusable.length === 0) return;
		const first = focusable[0];
		const last = focusable[focusable.length - 1];
		const active = document.activeElement;
		if (event.shiftKey && active === first) {
			event.preventDefault();
			last.focus();
		} else if (!event.shiftKey && active === last) {
			event.preventDefault();
			first.focus();
		}
	}
	/** @returns {HTMLElement[]} */
	#focusableElements() {
		if (!this.#popup) return [];
		return Array.from(this.#popup.querySelectorAll("button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex=\"-1\"])")).filter((el) => el instanceof HTMLElement && el.offsetParent !== null);
	}
	/** @returns {HTMLElement[]} */
	#staggerItems() {
		if (!this.#popup) return [];
		return [
			"[data-vp-sticky-kenteken-close]",
			".kenteken-panel__brand-copy",
			".kenteken-panel__brand-media",
			".kenteken-panel__plate",
			".kenteken-panel__field",
			"[data-vp-sticky-kenteken-assortment]"
		].map((sel) => this.#popup?.querySelector(sel)).filter((el) => el instanceof HTMLElement && !el.hidden);
	}
	#restoreFocus() {
		const target = this.#lastFocus || this.#fab;
		this.#lastFocus = null;
		target?.focus?.({ preventScroll: true });
	}
	async #animateOpen() {
		const fab = this.#fab;
		const popup = this.#popup;
		const backdrop = this.#backdrop;
		if (!fab || !popup || !backdrop) return;
		this.#timeline?.kill();
		const items = this.#staggerItems();
		if (prefersReducedMotion()) {
			fab.style.visibility = "hidden";
			fab.style.pointerEvents = "none";
			popup.style.opacity = "1";
			popup.style.transform = "none";
			backdrop.style.opacity = "1";
			items.forEach((el) => {
				el.style.opacity = "1";
				el.style.transform = "none";
			});
			this.#closeBtn?.focus({ preventScroll: true });
			return;
		}
		if (!this.#gsap) this.#gsap = await ensureGsap();
		const gsap = this.#gsap;
		if (!gsap) {
			fab.style.visibility = "hidden";
			popup.style.opacity = "1";
			backdrop.style.opacity = "1";
			this.#closeBtn?.focus({ preventScroll: true });
			return;
		}
		gsap.set(popup, {
			opacity: 0,
			y: 72,
			clearProps: "scale,transformOrigin"
		});
		gsap.set(backdrop, { opacity: 0 });
		gsap.set(fab, { transformOrigin: "50% 50%" });
		if (items.length) gsap.set(items, {
			opacity: 0,
			y: 28
		});
		this.#timeline = gsap.timeline({ onComplete: () => {
			this.#closeBtn?.focus({ preventScroll: true });
		} });
		this.#timeline.to(fab, {
			scale: .7,
			y: 12,
			opacity: 0,
			duration: .28,
			ease: "power2.in",
			onComplete: () => {
				fab.style.visibility = "hidden";
				fab.style.pointerEvents = "none";
			}
		}, 0).to(backdrop, {
			opacity: 1,
			duration: .35,
			ease: "power2.out"
		}, .04).to(popup, {
			opacity: 1,
			y: 0,
			duration: .95,
			ease: SPRING
		}, .06);
		if (items.length) this.#timeline.to(items, {
			opacity: 1,
			y: 0,
			duration: .85,
			ease: SPRING_SOFT,
			stagger: .07
		}, .18);
	}
	async #animateClose() {
		const fab = this.#fab;
		const popup = this.#popup;
		const backdrop = this.#backdrop;
		if (!fab || !popup || !backdrop) return;
		this.#timeline?.kill();
		const items = this.#staggerItems();
		const modelSelect = this.querySelector("vp-model-select[open]");
		if (modelSelect instanceof HTMLElement) modelSelect.removeAttribute("open");
		if (prefersReducedMotion()) return;
		if (!this.#gsap) this.#gsap = await ensureGsap();
		const gsap = this.#gsap;
		if (!gsap) return;
		fab.style.visibility = "";
		fab.style.pointerEvents = "";
		gsap.set(fab, {
			opacity: 0,
			scale: .7,
			y: 12,
			transformOrigin: "50% 50%"
		});
		await new Promise((resolve) => {
			this.#timeline = gsap.timeline({
				onComplete: resolve,
				onInterrupt: resolve
			});
			if (items.length) this.#timeline.to(items, {
				opacity: 0,
				y: 16,
				duration: .18,
				ease: "power2.in",
				stagger: {
					each: .035,
					from: "end"
				}
			}, 0);
			this.#timeline.to(popup, {
				opacity: 0,
				y: 56,
				duration: .32,
				ease: "power2.in"
			}, items.length ? .06 : 0).to(backdrop, {
				opacity: 0,
				duration: .24,
				ease: "power2.in"
			}, .08).to(fab, {
				opacity: 1,
				scale: 1,
				y: 0,
				duration: .7,
				ease: SPRING_SOFT
			}, .16);
		});
		if (this.#open) return;
		gsap.set(popup, { clearProps: "opacity,transform,y" });
		gsap.set(backdrop, { clearProps: "opacity" });
		gsap.set(fab, { clearProps: "opacity,transform,transformOrigin,y,scale" });
		if (items.length) gsap.set(items, { clearProps: "opacity,transform,y" });
	}
};
if (!customElements.get("vp-sticky-kenteken")) customElements.define("vp-sticky-kenteken", VpStickyKenteken);
/**
* @param {import('@theme/kenteken-api').VpVehicle | null | undefined} vehicle
* @returns {boolean}
*/
function vehicleQualifiesForFabMedia(vehicle) {
	if (!vehicle) return false;
	if (vehicle.kenteken && isCompleteKenteken(vehicle.kenteken)) return true;
	if (!vehicle.kenteken && (vehicle.brandAndModel || vehicle.chassisCode)) return true;
	return false;
}
/**
* @param {Element | null | undefined} panel
* @returns {Record<string, string>}
*/
function readCosyImagesFromPanel(panel) {
	return parseJsonObject((panel?.querySelector("[data-kenteken-cosy-images]") || document.querySelector("[data-kenteken-cosy-images]"))?.textContent);
}
/**
* @param {string|null|undefined} text
* @returns {Record<string, string>}
*/
function parseJsonObject(text) {
	const raw = String(text || "");
	const start = raw.indexOf("{");
	const end = raw.lastIndexOf("}");
	if (start < 0 || end <= start) return {};
	try {
		const parsed = JSON.parse(raw.slice(start, end + 1));
		return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
	} catch {
		return {};
	}
}
/**
* @param {string} url
* @returns {string}
*/
function normalizeFabImageUrl(url) {
	const value = String(url || "").trim();
	if (!value) return "";
	if (value.startsWith("//")) return `https:${value}`;
	return value;
}
/**
* @param {string} url
* @returns {Promise<void>}
*/
function preloadFabImage(url) {
	const src = normalizeFabImageUrl(url);
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.referrerPolicy = "no-referrer";
		img.onload = () => {
			if (img.naturalWidth < 64 || img.naturalHeight < 64) {
				reject(/* @__PURE__ */ new Error("image_too_small"));
				return;
			}
			resolve();
		};
		img.onerror = () => reject(/* @__PURE__ */ new Error("image_error"));
		img.src = src;
	});
}
//#endregion
